import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText } from "@/lib/api";
import { money0 } from "@/lib/partners";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowDown, Flame } from "lucide-react";

const Stat = ({ label, value, sub, gold }) => (
  <div className="bg-white border border-gray-200 p-6">
    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</p>
    <p className={`font-display text-3xl mt-2 ${gold ? "text-[#c19a6b]" : ""}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

export default function PartnerFunnel() {
  const [funnel, setFunnel] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("");

  const load = useCallback(() => {
    api.get("/realtors/funnel").then((r) => setFunnel(r.data)).catch((e) => toast.error(errText(e)));
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const createCampaign = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/campaigns", { name });
      setName("");
      toast.success("Campaign created");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!funnel) return <AdminLayout title="Partner Funnel"><p className="text-gray-400">Loading...</p></AdminLayout>;

  const base = funnel.presentations_sent || funnel.realtors || 0;
  const pct = (n) => (base ? `${((n / base) * 100).toFixed(base > 100 ? 2 : 1)}%` : "—");

  const stages = [
    { label: "Realtors in Database", value: funnel.realtors, note: "total active records" },
    { label: "Presentations Sent", value: funnel.presentations_sent, note: "marked contacted" },
    { label: "Presentation Views", value: funnel.presentation_views, note: `${funnel.total_presentation_views} total views · ${pct(funnel.presentation_views)}` },
    { label: "Interested", value: funnel.interested, note: pct(funnel.interested) },
    { label: "Partners", value: funnel.partners, note: pct(funnel.partners) },
    { label: "Activated Partners", value: funnel.activated, note: pct(funnel.activated) },
    { label: "Client Leads", value: funnel.client_leads, note: "private client requests" },
    { label: "Quotes", value: funnel.quotes, note: "partner-priced quotes" },
    { label: "Sales", value: funnel.sales, note: pct(funnel.sales) },
  ];

  return (
    <AdminLayout title="Realtor Acquisition Funnel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="funnel-stats">
        <Stat label="Attributed Revenue" value={money0(funnel.revenue_cents)} gold />
        <Stat label="Est. Gross Profit" value={money0(funnel.gross_profit_cents)} sub="using your gross margin setting" />
        <Stat label="Client Savings Generated" value={money0(funnel.client_savings_cents)} sub={`${funnel.pieces} pieces revived`} />
        <Stat label="Hot Prospects" value={funnel.hot} sub="recently engaged" />
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-2xl mb-6">The Funnel</h2>
          <div className="space-y-1" data-testid="funnel-stages">
            {stages.map((s, i) => (
              <div key={s.label}>
                <div className="bg-white border border-gray-200 px-6 py-4 flex items-center justify-between" style={{ marginLeft: `${i * 8}px` }}>
                  <div>
                    <p className="text-sm font-semibold tracking-wide">{s.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.note}</p>
                  </div>
                  <span className="font-display text-2xl">{s.value}</span>
                </div>
                {i < stages.length - 1 && (
                  <div className="flex justify-center py-1" style={{ marginLeft: `${i * 8}px` }}>
                    <ArrowDown size={14} className="text-[#c19a6b]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl mb-6">Campaigns</h2>
          <div className="flex gap-2 mb-5">
            <Input
              data-testid="campaign-name-input"
              placeholder="September Realtor Sprint"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 rounded-sm bg-white"
            />
            <Button data-testid="create-campaign-btn" onClick={createCampaign} className="h-11 px-6 bg-black hover:bg-black/85 text-white rounded-sm text-xs uppercase tracking-[0.15em] btn-lift">
              Add
            </Button>
          </div>
          {campaigns.length === 0 && <p className="text-sm text-gray-400">No campaigns yet. Create one, then assign realtors from the partner list.</p>}
          <div className="space-y-3" data-testid="campaign-list">
            {campaigns.map((c) => (
              <div key={c.id} className="bg-white border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <p className="font-display text-lg">{c.name}</p>
                  <span className="text-xs text-gray-400">{c.stats.realtors} realtors</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4 text-center">
                  {[
                    ["Views", c.stats.presentation_views],
                    ["Partners", c.stats.partners],
                    ["Activated", c.stats.activated],
                    ["Leads", c.stats.client_leads],
                    ["Sales", c.stats.sales],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="font-display text-xl">{v}</p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400">{l}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#8a6a42] mt-4">
                  {money0(c.stats.revenue_cents)} revenue · {money0(c.stats.gross_profit_cents)} est. gross profit
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-black text-white p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#c19a6b] mb-3 flex items-center gap-2"><Flame size={13} /> Next best action</p>
            <p className="text-sm text-white/70 leading-relaxed">
              Realtors who recently opened their presentation are flagged Hot in the partner list. Call them while
              they're still on the page.
            </p>
            <Button asChild className="mt-5 h-11 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-xs uppercase tracking-[0.15em] btn-lift">
              <Link to="/admin/realtors">Open Realtor Partners</Link>
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
